import React from 'react';
import PropTypes from 'prop-types';
import { Box, Card, Typography, Stack, Button, Chip } from '@mui/material';
import LockOutlined from '@ant-design/icons/LockOutlined';
import ThunderboltFilled from '@ant-design/icons/ThunderboltFilled';
import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from 'contexts/SubscriptionContext';

const MODULE_NAMES = {
  billing: 'Billing & Invoicing',
  pharmacy: 'Pharmacy & POS',
  lab_requests: 'Lab Workflows & Diagnostics'
};

const MODULE_DESCRIPTIONS = {
  billing: 'Itemized invoices, POS counter billing, GST calculation, expense logs, and printable receipts.',
  pharmacy: 'Prescription fulfillment, medicine stock inventory, expiry tracking, and pharmacy counter sales.',
  lab_requests: 'Diagnostic test requests, sample collection tracking, report uploads, and lab workflows.'
};

export default function SubscriptionModuleGuard({ module, children }) {
  const navigate = useNavigate();
  const { isModuleUnlocked, openCheckout, plans } = useSubscription();

  const isUnlocked = isModuleUnlocked(module);

  if (isUnlocked) {
    return children;
  }

  const moduleName = MODULE_NAMES[module] || 'Advanced Feature';
  const moduleDesc = MODULE_DESCRIPTIONS[module] || 'This feature is part of our advanced clinic plans.';
  const upgradePlan = plans?.find((p) => p.tier === 'professional') || plans?.[1];

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 4 },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '70vh'
      }}
    >
      <Card
        elevation={0}
        sx={{
          maxWidth: 580,
          width: '100%',
          p: { xs: 3, sm: 4.5 },
          borderRadius: 4,
          border: '1.5px solid #e2e8f0',
          textAlign: 'center',
          boxShadow: '0 10px 30px rgba(0,0,0,0.04)'
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'rgba(245, 158, 11, 0.12)',
            color: '#b45309',
            display: 'grid',
            placeItems: 'center',
            mx: 'auto',
            mb: 2.5
          }}
        >
          <LockOutlined style={{ fontSize: 36 }} />
        </Box>

        <Chip
          label="Starter Plan Limitation"
          color="warning"
          size="small"
          sx={{ fontWeight: 800, mb: 1.5, px: 1 }}
        />

        <Typography variant="h3" fontWeight={900} color="text.primary" gutterBottom>
          {moduleName} is Locked
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          {moduleDesc} Your clinic is currently on the <strong>Starter Plan (₹50/month)</strong> which includes core clinical modules (Doctors, Appointments & Patients).
        </Typography>

        <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1', mb: 3.5, textAlign: 'left' }}>
          <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase" display="block" sx={{ mb: 0.5 }}>
            Upgrade Option
          </Typography>
          <Typography variant="body2" color="text.primary" fontWeight={700}>
            {upgradePlan?.name || 'Professional'} Plan • {upgradePlan?.pricing?.monthly?.displayPrice || '₹2,499'}/month
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Unlocks Billing, Pharmacy POS, Lab Workflows, and priority 2-hour support.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            sx={{ fontWeight: 700, borderRadius: 2.5, px: 2.5, py: 1 }}
          >
            Go Back
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={<ThunderboltFilled />}
            onClick={() => {
              if (upgradePlan) {
                openCheckout(upgradePlan);
              } else {
                navigate('/subscription');
              }
            }}
            sx={{ fontWeight: 800, borderRadius: 2.5, px: 3.5, py: 1 }}
          >
            Upgrade to Unlock
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}

SubscriptionModuleGuard.propTypes = {
  module: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};
